/**
 * Transfer service — wraps GET /api/transfer_process_log.
 * Parity target: Angular `loadTransfers()` in `transfer-process-list.component.ts`.
 */

import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import { getAuthTokenFromStorage } from '@/lib/utils/storage'
import { getBrowserApiBaseUrl } from '@/config/env'
import type {
  TransferListParams,
  TransferListResponse,
} from '../types/transfer'

function appendIfPresent(sp: URLSearchParams, key: string, value: unknown): void {
  if (value === undefined || value === null || value === '') return
  sp.append(key, String(value))
}

/**
 * Build query string for GET `/api/transfer_process_log`.
 * Uses `URLSearchParams` (like `applicationService`) so all values are explicit strings — avoids
 * axios object-serialization edge cases with booleans / mixed types.
 */
export function buildTransferQueryParams(params: TransferListParams): URLSearchParams {
  const sp = new URLSearchParams()
  sp.append('listing', 'true')
  const entries: (keyof TransferListParams)[] = [
    'page',
    'perPage',
    'sortBy',
    'orderBy',
    'search',
    'status',
    'property_id',
    'company_id',
    'org_id',
    'process_name',
    'fk_team_id',
    'fromDt',
    'toDt',
  ]
  for (const key of entries) {
    appendIfPresent(sp, key, params[key])
  }
  if (sp.has('property_id') && sp.has('company_id')) {
    sp.delete('company_id')
  }
  return sp
}

export async function getTransfers(
  params: TransferListParams,
): Promise<TransferListResponse> {
  try {
    const query = buildTransferQueryParams(params)
    const response = await apiClient.get<TransferListResponse>(
      '/api/transfer_process_log',
      { params: query },
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load transfers')
  }
}

/**
 * Build export URL for GET `/api/export/transfer_process_log`.
 * Matches Angular `transfer-process-list.component.ts` export().
 */
export function getTransferExportUrl(params: TransferListParams): string | null {
  const token = getAuthTokenFromStorage()
  if (!token) return null
  const base = getBrowserApiBaseUrl()
  const sp = new URLSearchParams()
  const entries: (keyof TransferListParams)[] = [
    'search',
    'org_id',
    'company_id',
    'property_id',
    'status',
    'process_name',
    'fk_team_id',
    'fromDt',
    'toDt',
  ]
  for (const key of entries) {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      sp.append(key, String(params[key]))
    }
  }
  if (sp.has('property_id') && sp.has('company_id')) {
    sp.delete('company_id')
  }
  sp.append('access_token', token)
  sp.append('view', 'xlsx')
  return `${base}/api/export/transfer_process_log?${sp.toString()}`
}
